import { useEffect, useRef, useState, type ReactNode } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  LinearProgress,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigate, Link as RouterLink, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  commentsRequired,
  openedWhereRequired,
  problemsIn,
  type Assessment,
  type AssessmentId,
  type JsonValue,
} from "@thalamos/haven-common";

import {
  answered,
  answerOf,
  domainChangesBetween,
  problemsAt,
  radioValueOf,
  type DomainChange,
  type ItemElement,
  type QuestionAnswer,
} from "../../components/AssessmentForm/answering.js";
import { ControlFor } from "../../components/AssessmentForm/AssessmentForm.js";
import { ChoiceOptionLabel } from "../../components/AssessmentForm/ChoiceOptionLabel.js";
import {
  ItemComment,
  type CommentSave,
} from "../../components/ItemComment/ItemComment.js";
import { PreviousComment } from "../../components/PreviousComment/PreviousComment.js";
import {
  FORM_LANGUAGE,
  useAssessment,
  type OpenedForm,
  type Opening,
  type SaveOutcome,
} from "./AssessmentContext.js";
import {
  domainOf,
  landingItemOf,
  type AssessmentDomain,
} from "./assessmentDomain.js";
import {
  WithAssessmentFrame,
  type AssessmentFrame,
  type AssessmentPaths,
} from "./AssessmentFrame.js";

/** Where the latest rating chosen on a screen stands: on its way, kept, or not kept. */
export type RatingSave = "saving" | SaveOutcome;

/**
 * What a screen inside an assessment shows in place of its own content while
 * the form is not open on it: the assessment is no longer open, the form is
 * still opening, or it could not be opened.
 */
const NotOpened = ({
  assessment,
  opening,
  paths,
}: {
  assessment: Assessment;
  opening: Opening;
  paths: AssessmentPaths;
}) => {
  const { t } = useTranslation();

  if (assessment.status !== "open") {
    const away = paths.away(assessment);

    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="info">{t("assessment.overview.notOpen")}</Alert>
        {away === undefined ? null : (
          <Link component={RouterLink} to={away}>
            {t("assessment.overview.toPersonPage")}
          </Link>
        )}
      </Stack>
    );
  }

  return opening.kind === "failed" ? (
    <Alert severity="error">
      {t(`assessment.page.failed.${opening.reason}`)}
    </Alert>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {t("common.loading")}
    </Typography>
  );
};

/** Says what the address names is not there, and leads back to the overview. */
const Missing = ({
  assessmentId,
  message,
  paths,
}: {
  assessmentId: AssessmentId;
  message: string;
  paths: AssessmentPaths;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Alert severity="warning">{message}</Alert>
      <Link component={RouterLink} to={paths.overview(assessmentId)}>
        {t("assessment.item.backToOverview")}
      </Link>
    </Stack>
  );
};

/** Opening a domain: redirects to its `landingItemOf`. */
export const AssessmentDomainLanding = () => (
  <WithAssessmentFrame>
    {({ paths }) => <AssessmentDomainLandingIn paths={paths} />}
  </WithAssessmentFrame>
);

const AssessmentDomainLandingIn = ({ paths }: { paths: AssessmentPaths }) => {
  const { t } = useTranslation();
  const { group = "" } = useParams();
  const { assessment, opening } = useAssessment();

  if (assessment === undefined) {
    return null;
  }

  if (assessment.status !== "open" || opening.kind !== "open") {
    return (
      <NotOpened assessment={assessment} opening={opening} paths={paths} />
    );
  }

  const lookup = domainOf(opening.opened, group);

  if (lookup.kind === "unavailable") {
    return (
      <Alert severity="error">{t("assessment.page.failed.unavailable")}</Alert>
    );
  }

  if (lookup.kind === "unknown") {
    return (
      <Missing
        assessmentId={assessment.id}
        message={t("assessment.item.unknownDomain")}
        paths={paths}
      />
    );
  }

  const landing = landingItemOf(lookup.domain);

  return landing === undefined ? (
    <Missing
      assessmentId={assessment.id}
      message={t("assessment.item.emptyDomain")}
      paths={paths}
    />
  ) : (
    <Navigate replace to={paths.item(assessment.id, group, landing)} />
  );
};

/**
 * One item of a domain, answered on a page of its own. A rating is saved the
 * moment it is chosen; any other answer, and a comment, once typing pauses.
 */
export const AssessmentItemPage = () => (
  <WithAssessmentFrame>
    {(frame) => <AssessmentItemPageIn frame={frame} />}
  </WithAssessmentFrame>
);

const AssessmentItemPageIn = ({ frame }: { frame: AssessmentFrame }) => {
  const { group = "", item = "" } = useParams();
  const { assessment, opening } = useAssessment();

  if (assessment === undefined) {
    return null;
  }

  if (assessment.status !== "open" || opening.kind !== "open") {
    return (
      <NotOpened
        assessment={assessment}
        opening={opening}
        paths={frame.paths}
      />
    );
  }

  return (
    <AssessmentItemPageFor
      // Keyed by the item, so what one item's rating did is never shown on
      // the next.
      key={`${group}/${item}`}
      assessment={assessment}
      opened={opening.opened}
      group={group}
      item={item}
      frame={frame}
    />
  );
};

const AssessmentItemPageFor = ({
  assessment,
  opened,
  group,
  item,
  frame: { paths, isOwner },
}: {
  assessment: Assessment;
  opened: OpenedForm;
  group: string;
  item: string;
  frame: AssessmentFrame;
}) => {
  const { t } = useTranslation();
  const { save, unsavedAnswers, onDocumentChange, flushNow, onGroupOpened } =
    useAssessment();
  const [ratingSave, setRatingSave] = useState<RatingSave>();
  const [domainChanges, setDomainChanges] = useState<readonly DomainChange[]>(
    [],
  );
  // What the latest rating to add or remove a domain did, until a save
  // carrying it lands.
  const unannounced = useRef<readonly DomainChange[] | undefined>(undefined);
  // Only the latest rating's save may say how saving went, since an earlier
  // one can settle after it.
  const choices = useRef(0);
  // The whole document is saved together, so any later save that lands
  // carries a rating whose own save failed.
  useEffect(() => {
    if (save === "saved") {
      setRatingSave((was) => (was === "failed" ? "saved" : was));

      if (unannounced.current !== undefined) {
        setDomainChanges(unannounced.current);
        unannounced.current = undefined;
      }
    }
  }, [save]);

  const lookup = domainOf(opened, group);
  const editable =
    isOwner?.(assessment) === true && assessment.status === "open";
  // Landing on any item opens its domain, which a draft that carried ratings
  // over needs of every domain before it can be finished.
  const waitingToOpen = openedWhereRequired(opened.draft);
  const opensTheDomain =
    editable &&
    lookup.kind === "found" &&
    lookup.domain.items.some(({ element }) => element.at.item === item) &&
    waitingToOpen !== undefined &&
    !waitingToOpen.has(group);
  useEffect(() => {
    if (opensTheDomain) {
      void onGroupOpened(group);
    }
  }, [opensTheDomain, onGroupOpened, group]);

  if (lookup.kind === "unavailable") {
    return (
      <Alert severity="error">{t("assessment.page.failed.unavailable")}</Alert>
    );
  }

  if (lookup.kind === "unknown") {
    return (
      <Missing
        assessmentId={assessment.id}
        message={t("assessment.item.unknownDomain")}
        paths={paths}
      />
    );
  }

  // Every page inside the app has a session, so a viewer not yet known is one
  // still being read; until then it cannot be told whether they may rate.
  if (isOwner === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("common.loading")}
      </Typography>
    );
  }

  const { domain } = lookup;
  const index = domain.items.findIndex(
    ({ element }) => element.at.item === item,
  );
  const current = domain.items[index];

  if (current === undefined) {
    return (
      <Missing
        assessmentId={assessment.id}
        message={t("assessment.item.unknownItem")}
        paths={paths}
      />
    );
  }

  const { element } = current;
  const { schema, form, draft } = opened;
  const answer = answerOf(draft.document, element);
  const carried =
    draft.carriedRatings?.[element.at.group]?.[element.at.item]?.response;
  const lastTime = opened.previous;
  const commentLeft = lastTime?.comments.find(
    (each) => each.group === element.at.group && each.item === element.at.item,
  );
  const lastComment =
    lastTime === undefined || commentLeft === undefined
      ? undefined
      : { comment: commentLeft.comment, finishedAt: lastTime.finishedAt };
  // Errs only where no validator can be built from the schema, which the
  // problems already report; meanwhile no comment is marked required.
  const commentRequired = commentsRequired({
    schema,
    form,
    document: draft.document,
    context: draft.context,
  })
    .unwrapOr(new Set<string>())
    .has(element.at.item);
  const commentKey = `${group}/${item}/comment`;
  // The context reports one save state for the whole document, so the
  // comment's own is that state while its change is not yet kept.
  const commentSave: CommentSave | undefined = unsavedAnswers.has(commentKey)
    ? save === "failed"
      ? "failed"
      : "saving"
    : (answer?.comment ?? "").trim() === ""
      ? undefined
      : "saved";
  // "Answer this question" is what any unanswered item would say, so only
  // what is wrong with an answer given is shown.
  const problems = problemsIn({
    schema,
    form,
    document: draft.document,
    context: draft.context,
  })
    .map((found) =>
      problemsAt(found, element).filter(
        ({ code }) => code !== "must-be-answered",
      ),
    )
    .unwrapOr([])
    .map((problem) =>
      t(`assessment.form.problems.${problem.code}`, {
        ...problem.params,
        defaultValue: problem.code,
      }),
    );
  const pathOf = (at: number) => {
    const target = domain.items[at];

    return target === undefined
      ? undefined
      : paths.item(assessment.id, group, target.element.at.item);
  };
  const change = (next: QuestionAnswer | undefined, changed?: string) => {
    const document = answered(
      { schema, form, context: draft.context },
      draft.document,
      element,
      next,
    );

    if (changed === undefined) {
      onDocumentChange(document);
    } else {
      onDocumentChange(document, changed);
    }

    return document;
  };
  const saveRatingNow = async () => {
    choices.current += 1;
    const choice = choices.current;
    setRatingSave("saving");
    const outcome = await flushNow();

    if (choice === choices.current) {
      setRatingSave(outcome);

      if (outcome === "saved" && unannounced.current !== undefined) {
        setDomainChanges(unannounced.current);
        unannounced.current = undefined;
      }
    }
  };

  return (
    <AssessmentItemPageContent
      domain={domain}
      element={element}
      position={index + 1}
      answer={answer}
      carried={carried}
      lastComment={lastComment}
      editable={editable}
      problems={problems}
      ratingSave={ratingSave}
      domainChanges={domainChanges}
      commentRequired={commentRequired}
      commentSave={commentSave}
      overview={paths.overview(assessment.id)}
      previous={pathOf(index - 1)}
      next={pathOf(index + 1)}
      onRate={async (response) => {
        const document = change({ ...answer, response });
        const changes = domainChangesBetween(
          { schema, form, context: draft.context },
          { before: draft.document, after: document },
        );

        // A rating that adds or removes no domain leaves what an earlier one
        // said; one that does clears it at once and says its own once its
        // save lands.
        if (changes.length > 0) {
          setDomainChanges([]);
          unannounced.current = changes;
        }

        await saveRatingNow();
      }}
      onAnswer={(response) => {
        // A comment says something about the answer, so clearing the answer
        // clears the comment with it.
        change(response === undefined ? undefined : { ...answer, response });
      }}
      onComment={(comment) => {
        // A comment says something about the rating, so waits for one.
        if (answer !== undefined) {
          change(
            comment === ""
              ? { response: answer.response }
              : { ...answer, comment },
            commentKey,
          );
        }
      }}
      onRetry={saveRatingNow}
      onCopy={async (text) => {
        await navigator.clipboard.writeText(text);
      }}
      onCommentRetry={async () => {
        await flushNow();
      }}
    />
  );
};

/** A choice's options as cards, one per value, each a radio. */
const RatingOptions = ({
  options,
  labelledBy,
  chosen,
  carried,
  onRate,
}: {
  options: Extract<
    ItemElement["presentation"]["control"],
    { kind: "choice" }
  >["options"];
  labelledBy: string;
  chosen: JsonValue | undefined;
  /** The rating carried over, whose option keeps its tag whatever is chosen. */
  carried: JsonValue | undefined;
  onRate: (response: JsonValue) => void;
}) => {
  const { t } = useTranslation();
  const chosenValue = chosen === undefined ? "" : radioValueOf(chosen);
  const carriedValue =
    carried === undefined ? undefined : radioValueOf(carried);

  return (
    <RadioGroup
      aria-labelledby={labelledBy}
      value={chosenValue}
      onChange={(_event, picked) => {
        const option = options.find(
          (each) => radioValueOf(each.value) === picked,
        );

        // A radio group offers no way back to nothing, so a rating once given
        // can be changed but never cleared.
        if (option !== undefined) {
          onRate(option.value);
        }
      }}
    >
      <Stack spacing={1}>
        {options.map((option) => {
          const value = radioValueOf(option.value);
          const carriedHere = value === carriedValue;
          const tagId = `${labelledBy}-carried`;

          return (
            <Paper
              key={value}
              variant="outlined"
              sx={{
                px: 2,
                py: 1,
                bgcolor: value === chosenValue ? "action.selected" : undefined,
              }}
            >
              <FormControlLabel
                value={value}
                control={
                  <Radio
                    slotProps={{
                      input: {
                        "aria-describedby": carriedHere ? tagId : undefined,
                      },
                    }}
                  />
                }
                label={<ChoiceOptionLabel option={option} />}
                sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
              />
              {carriedHere ? (
                <Chip
                  id={tagId}
                  size="small"
                  variant="outlined"
                  label={t("assessment.item.carriedRating")}
                  sx={{ ml: 4, mb: 0.5 }}
                />
              ) : null}
            </Paper>
          );
        })}
      </Stack>
    </RadioGroup>
  );
};

/** How the latest rating's save went, with a way to try again once it failed. */
const RatingSaveStatus = ({
  ratingSave,
  onRetry,
}: {
  ratingSave: RatingSave | undefined;
  onRetry: () => Promisable<void>;
}) => {
  const { t } = useTranslation();

  return (
    <Box aria-live="polite">
      {ratingSave === "failed" ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void onRetry();
              }}
            >
              {t("assessment.item.tryAgain")}
            </Button>
          }
        >
          {t("assessment.item.save.failed")}
        </Alert>
      ) : ratingSave === undefined ? null : (
        <Typography variant="body2" color="text.secondary">
          {t(`assessment.item.save.${ratingSave}`)}
        </Typography>
      )}
    </Box>
  );
};

/** What the latest rating did to the assessment's domains. */
const DomainChangeNotices = ({
  domainChanges,
}: {
  domainChanges: readonly DomainChange[];
}) => {
  const { t } = useTranslation();

  // The region is there before any message, since a screen reader may not
  // announce one that arrives with its text already in it. Its alerts give up
  // their own role so each message is announced once, politely, by the region.
  return (
    <Stack
      role="status"
      spacing={1}
      sx={{ mt: domainChanges.length === 0 ? 0 : 2 }}
    >
      {domainChanges.map((domainChange) =>
        match(domainChange)
          .with({ kind: "added" }, ({ title }) => (
            <Alert
              key={`added/${title}`}
              role="none"
              severity="success"
              icon={<AddCircleOutlineIcon />}
            >
              <strong>
                {t("assessment.item.domainChange.added", { domain: title })}
              </strong>
            </Alert>
          ))
          .with({ kind: "removed" }, ({ title, answersDropped }) => (
            <Alert
              key={`removed/${title}`}
              role="none"
              severity="info"
              icon={<RemoveCircleOutlineIcon />}
            >
              <strong>
                {t("assessment.item.domainChange.removed", { domain: title })}
              </strong>
              {answersDropped === 0
                ? null
                : ` ${t("assessment.item.domainChange.answersDropped", { count: answersDropped })}`}
            </Alert>
          ))
          .exhaustive(),
      )}
    </Stack>
  );
};

/** A labelled value shown as text, for a reader who cannot change it. */
const ReadOnlyField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    {children}
  </Box>
);

/**
 * One item's page, with everything it shows injected so a story or a test can
 * show any state of it without an API client.
 *
 * Where it is not `editable`, the rating and the comment are shown as text.
 */
export const AssessmentItemPageContent = ({
  domain,
  element,
  position,
  answer,
  carried,
  lastComment,
  editable,
  problems,
  ratingSave,
  domainChanges,
  commentRequired,
  commentSave,
  overview,
  previous,
  next,
  onRate,
  onAnswer,
  onComment,
  onRetry,
  onCommentRetry,
  onCopy,
}: {
  domain: AssessmentDomain;
  element: ItemElement;
  /** Where the item stands among the items its domain asks, counted from one. */
  position: number;
  answer: QuestionAnswer | undefined;
  /** The rating carried over from the last assessment, where one was. */
  carried: JsonValue | undefined;
  /** Last assessment's comment on the item, and when it finished, where there is one. */
  lastComment: { comment: string; finishedAt: Date } | undefined;
  editable: boolean;
  /** What is wrong with the answer given, in words; empty where nothing is. */
  problems: readonly string[];
  /** How the latest rating chosen here is being saved; undefined before one is. */
  ratingSave: RatingSave | undefined;
  /** What the latest rating chosen here that adds or removes a domain did, once its save has landed; empty until then. */
  domainChanges: readonly DomainChange[];
  /** Whether the rules make a comment mandatory for the answer given. */
  commentRequired: boolean;
  /** How the latest change to the comment is being saved; undefined while none is written. */
  commentSave: CommentSave | undefined;
  overview: string;
  /** The path of the item before; undefined on the first. */
  previous: string | undefined;
  /** The path of the item after; undefined on the last. */
  next: string | undefined;
  /** A choice, saved at once. */
  onRate: (response: JsonValue) => Promisable<void>;
  /** Any other kind of answer, as it is typed; `undefined` is none at all. */
  onAnswer: (response: JsonValue | undefined) => Promisable<void>;
  onComment: (comment: string) => Promisable<void>;
  onRetry: () => Promisable<void>;
  onCommentRetry: () => Promisable<void>;
  /** Puts text on the clipboard. */
  onCopy: (text: string) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const commentBox = useRef<HTMLTextAreaElement>(null);
  const { control, acceptsComment, title, description } = element.presentation;
  const titleId = `item-${element.at.group}-${element.at.item}-title`;
  const count = domain.items.length;
  const chosenOption =
    control.kind === "choice" && answer !== undefined
      ? control.options.find(
          (option) =>
            radioValueOf(option.value) === radioValueOf(answer.response),
        )
      : undefined;

  return (
    <Stack spacing={2} sx={{ p: { xs: 0, md: 3 } }}>
      <Box>
        <Button
          component={RouterLink}
          to={overview}
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          {t("assessment.item.backToOverview")}
        </Button>
      </Box>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography variant="overline" component="p">
                  {domain.title}
                </Typography>
                {domain.description === undefined ? null : (
                  <Typography variant="body2" color="text.secondary">
                    {domain.description}
                  </Typography>
                )}
                <Typography
                  id={titleId}
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: "bold" }}
                >
                  {title.text}
                </Typography>
              </Box>
              <Typography color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {t("assessment.item.position", { position, count })}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(position / count) * 100}
              aria-label={t("assessment.item.progress", { position, count })}
            />
            {description === undefined ? null : (
              <Typography>{description.text}</Typography>
            )}

            {!editable ? (
              control.kind === "choice" ? (
                <ReadOnlyField label={t("assessment.item.rating")}>
                  {chosenOption === undefined ? (
                    <Typography>{t("assessment.item.notRated")}</Typography>
                  ) : (
                    <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
                      <ChoiceOptionLabel option={chosenOption} />
                    </Paper>
                  )}
                </ReadOnlyField>
              ) : (
                <ReadOnlyField label={t("assessment.item.answer")}>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {answer === undefined
                      ? t("assessment.item.notAnswered")
                      : typeof answer.response === "string"
                        ? answer.response
                        : JSON.stringify(answer.response)}
                  </Typography>
                </ReadOnlyField>
              )
            ) : control.kind === "choice" ? (
              <>
                <RatingOptions
                  options={control.options}
                  labelledBy={titleId}
                  chosen={answer?.response}
                  carried={carried}
                  onRate={(response) => {
                    void onRate(response);
                  }}
                />
                {problems.length === 0 ? null : (
                  <Typography variant="body2" color="error">
                    {problems.join(" ")}
                  </Typography>
                )}
                {/* Both live regions render even while empty, so they sit in one child of the enclosing Stack to take one gap, not an empty second one. */}
                <Box>
                  <RatingSaveStatus ratingSave={ratingSave} onRetry={onRetry} />
                  <DomainChangeNotices domainChanges={domainChanges} />
                </Box>
              </>
            ) : (
              <ControlFor
                control={control}
                id={`item-${element.at.group}-${element.at.item}`}
                labelledBy={titleId}
                required={element.asking.required !== "no"}
                value={answer?.response}
                error={problems.length === 0 ? undefined : problems.join(" ")}
                onChange={(response) => {
                  void onAnswer(response);
                }}
              />
            )}

            {acceptsComment && editable && lastComment !== undefined ? (
              <PreviousComment
                comment={lastComment.comment}
                finishedAt={lastComment.finishedAt}
                canAdd={answer !== undefined}
                onAdd={async () => {
                  const written = answer?.comment ?? "";

                  await onComment(
                    written.trim() === ""
                      ? lastComment.comment
                      : `${written.trimEnd()}\n${lastComment.comment}`,
                  );
                  commentBox.current?.focus();
                }}
                onCopy={onCopy}
              />
            ) : null}

            {acceptsComment ? (
              <ItemComment
                inputRef={commentBox}
                value={answer?.comment ?? ""}
                required={commentRequired}
                response={answer?.response}
                language={FORM_LANGUAGE}
                saveState={commentSave}
                // A comment is about an answer, so there is nothing to say
                // until one is given.
                disabled={answer === undefined}
                readOnly={!editable}
                onChange={onComment}
                onRetry={onCommentRetry}
              />
            ) : null}

            <Stack direction="row" justifyContent="space-between">
              {previous === undefined ? (
                <span />
              ) : (
                <Button variant="outlined" component={RouterLink} to={previous}>
                  {t("assessment.item.back")}
                </Button>
              )}
              <Button
                variant="contained"
                component={RouterLink}
                to={next ?? overview}
              >
                {t(
                  next === undefined
                    ? "assessment.item.returnToOverview"
                    : "assessment.item.next",
                )}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};
